import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../api';
import './ChatAttachment.css';

export default function ChatAttachment({ message }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const dialog = useRef(null);
  const trigger = useRef(null);
  const name = message.attachment_name || 'Attachment';

  useEffect(() => {
    if (!open) return;
    const element = dialog.current;
    const opener = trigger.current;
    element.showModal();
    return () => {
      element.close();
      opener?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let objectUrl;
    setPreview(null);
    setError('');
    api.get(`/chat/messages/${message.id}/attachment`, { responseType: 'blob' })
      .then(response => {
        if (cancelled) return;
        const type = (response.headers?.['content-type'] || message.attachment_mime || '').split(';')[0].trim().toLowerCase();
        if (!['image/jpeg', 'image/png', 'application/pdf'].includes(type)) {
          throw new Error('Unsupported attachment');
        }
        objectUrl = URL.createObjectURL(new Blob([response.data], { type }));
        setPreview({ url: objectUrl, type });
      })
      .catch(() => {
        if (!cancelled) setError('Unable to preview this attachment. Please try again.');
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, message.id, message.attachment_mime, attempt]);

  return <>
    <button ref={trigger} type="button" onClick={() => setOpen(true)} className="mt-2 inline-block text-xs font-semibold text-teal-200 underline">
      Preview attachment ({name})
    </button>
    {open && createPortal(<dialog ref={dialog} className="chat-attachment-dialog" aria-label={`Attachment preview: ${name}`} onCancel={event => {
      event.preventDefault();
      setOpen(false);
    }}>
      <header className="chat-attachment-header">
        <div><span>ATTACHMENT PREVIEW</span><h2>{name}</h2></div>
        <button type="button" autoFocus onClick={() => setOpen(false)} aria-label="Close attachment preview">Close</button>
      </header>
      <div className="chat-attachment-content">
        {!preview && !error && <p role="status">Loading preview…</p>}
        {error && <div role="alert"><p>{error}</p><button type="button" onClick={() => setAttempt(value => value + 1)}>Try again</button></div>}
        {preview && !error && (preview.type.startsWith('image/')
          ? <img src={preview.url} alt={name} onError={() => setError('This image could not be displayed. Please try again.')} />
          : <iframe src={preview.url} title={`PDF preview: ${name}`} />)}
      </div>
      {preview && <footer className="chat-attachment-footer">
        <span>{preview.type === 'application/pdf' ? 'If your browser cannot display the PDF, you can save a copy.' : 'Full image preview'}</span>
        <a href={preview.url} download={name}>Download a copy</a>
      </footer>}
    </dialog>, document.body)}
  </>;
}
